import { useMutation } from '@apollo/client';
import { Checkbox, DOLink, SpinnerButton, Text, TextInput } from '@do/walrus';
import { Form, Formik } from 'formik';
import _get from 'lodash/get';
import React from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { LOGIN_REDIRECT_URL } from '../constants';
import copy from '../copy';
import {
  verifyDevice as VerifyDevice,
  verifyDeviceVariables
} from '../generated-types/verifyDevice';
import { VERIFY_DEVICE } from '../queries/queries';
import {
  addSessionData,
  handleFormError,
  saveSessionData
} from '../utils/gql-utils';
import {
  validationCopy,
  yupVerificationNumberValidation
} from '../utils/validation-utils';
import { ClickLink, ErrorBanner } from './Common';
import NewClickLink from './newBrandDesign/ClickLink';
import NewDOLink from './newBrandDesign/DoLink';
import NewErrorBanner from './newBrandDesign/ErrorBanner';
import NewSpinnerButton from './newBrandDesign/SpinnerButton';
import NewText from './newBrandDesign/Text';
import NewTextInput from './newBrandDesign/TextInput';
import NewTitle from './newBrandDesign/Title';

const Title = styled.h4``;

const VerificationValidation = Yup.object().shape({
  code: yupVerificationNumberValidation,
  trust_device: Yup.boolean()
});

const VerificationCheckbox = styled(Checkbox)`
  && {
    text-transform: initial;
    letter-spacing: initial;
    font-size: inherit;
  }
`;
interface Props {
  goBack: () => void;
  isNewBrandDesignEnabled: boolean;
}

const VerificationForm: React.FC<Props> = ({
  goBack,
  isNewBrandDesignEnabled
}) => {
  const [genericServerError, setGenericServerError] = React.useState(null);
  const [isRedirecting, setIsRedirecting] = React.useState(false);
  const [verifyDevice, { loading: isVerifyingDevice }] = useMutation<
    VerifyDevice,
    verifyDeviceVariables
  >(VERIFY_DEVICE);

  const TitleComponent = isNewBrandDesignEnabled ? NewTitle : Title;
  const TextComponent = isNewBrandDesignEnabled ? NewText : Text;
  const TextInputComponent = isNewBrandDesignEnabled ? NewTextInput : TextInput;
  const ErrorBannerComponent = isNewBrandDesignEnabled
    ? NewErrorBanner
    : ErrorBanner;
  const SpinnerButtonComponent = isNewBrandDesignEnabled
    ? NewSpinnerButton
    : SpinnerButton;
  const DOLinkComponent = isNewBrandDesignEnabled ? NewDOLink : DOLink;
  const ClickLinkComponent = isNewBrandDesignEnabled ? NewClickLink : ClickLink;

  return (
    <>
      <TitleComponent>{copy.form.verification.title}</TitleComponent>
      <TextComponent>{copy.form.verification.details}</TextComponent>

      <Formik
        initialValues={{
          code: '',
          trust_device: false
        }}
        validationSchema={VerificationValidation}
        onSubmit={(values, { setErrors }) => {
          setGenericServerError(null); /* Reset displayed error */

          verifyDevice({
            variables: {
              verifyDeviceRequest: addSessionData(values)
            }
          })
            .then((successResponse) => {
              const {
                status,
                redirect_url: redirectURL,
                session_data: sessionData
              } = _get(successResponse, 'data.verifyDevice');

              saveSessionData(sessionData);

              if (status === 'granted') {
                setIsRedirecting(true);
                window.location.assign(redirectURL || LOGIN_REDIRECT_URL);
              } else if (status === 'expired') {
                setGenericServerError(copy.errors.expired_verification_code);
              } else if (status === 'rejected') {
                setGenericServerError(copy.errors.invalid_verification_code);
              }
            })
            .catch(handleFormError(setErrors, setGenericServerError));
        }}
      >
        {({ errors, values, touched, setFieldValue }) => (
          <Form>
            {genericServerError && (
              <ErrorBannerComponent error={genericServerError} />
            )}
            <TextInputComponent
              className={isNewBrandDesignEnabled ? '' : 'do-u-mb--small'}
              label={validationCopy.verification.code_label}
              placeholder={`${copy.form.two_factor_auth.placeholder_prefix} ${validationCopy.verification.code_label}`}
              name="code"
              required
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              touched={touched.code}
              error={errors.code}
              value={values.code}
              onChange={(e: React.FormEvent<HTMLInputElement>) => {
                setFieldValue('code', e.currentTarget?.value?.trim());
              }}
            />
            <VerificationCheckbox
              className={isNewBrandDesignEnabled ? 'do-u-mt--small' : ''}
              name="trust_device"
              label={copy.form.verification.checkbox_label}
              checked={values.trust_device}
              error={errors.trust_device}
            />
            <SpinnerButtonComponent
              className={
                isNewBrandDesignEnabled ? 'do-u-mt--small' : 'do-u-mt--medium'
              }
              type="submit"
              fullWidth
              spinning={isVerifyingDevice || isRedirecting}
              disabled={isVerifyingDevice || isRedirecting}
              loadingMessage="Verifying your device"
            >
              {copy.form.verification.button_text}
            </SpinnerButtonComponent>
          </Form>
        )}
      </Formik>
      <Text
        type={isNewBrandDesignEnabled ? 'p' : 'h5'}
        className={
          isNewBrandDesignEnabled
            ? 'need-help-text'
            : 'do-u-mt--medium do-u-mb--small'
        }
      >
        {copy.form.verification.need_help_text}
      </Text>
      <TextComponent
        className={
          isNewBrandDesignEnabled ? 'do-u-mt--small' : 'do-u-mb--small'
        }
      >
        {copy.form.verification.checkout_text}{' '}
        <DOLinkComponent path="/docs/getting-started/faq/#dve">
          {copy.form.verification.troubleshooting_link}
        </DOLinkComponent>
        <> {copy.form.or} </>
        <ClickLinkComponent onClick={goBack}>
          {copy.form.verification.try_again_link}
        </ClickLinkComponent>
      </TextComponent>
    </>
  );
};

export default VerificationForm;
